import React from "react";
import { motion } from "motion/react";
import { ShieldCheck, Loader2, TrendingUp, FileSpreadsheet, ChevronRight } from "lucide-react";
import { PayrollIcon, VerifiedIcon } from "@/components/icons/domain";
import { DownloadIcon } from "@/components/icons/actions";
import { demoMonthName, demoMonthYear, ofMonth } from "../demoCalendar";

interface Props {
  reducedMotion?: boolean;
}

const rows = [
  { name: "Marie D.", base: "151h67", sup25: "2h00", sup50: "0h00", dim: "—", total: "157,40 €" },
  { name: "Lucas B.", base: "151h67", sup25: "4h30", sup50: "1h00", dim: "+30 %", total: "298,12 €" },
  { name: "Sarah M.", base: "120h00", sup25: "0h00", sup50: "0h00", dim: "—", total: "0,00 €" },
  { name: "Tom P.", base: "100h00", sup25: "1h15", sup50: "0h00", dim: "—", total: "62,50 €" },
];

const wizardSteps = [
  { id: 1, label: "Régularisations" },
  { id: 2, label: "Récapitulatif" },
  { id: 3, label: "Déclarations" },
  { id: 4, label: "Export" },
];

export const ReportScene: React.FC<Props> = ({ reducedMotion }) => {
  const [visibleRows, setVisibleRows] = React.useState(reducedMotion ? rows.length : 0);
  const [ready, setReady] = React.useState(reducedMotion);

  React.useEffect(() => {
    if (reducedMotion) return;
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    rows.forEach((_, i) => {
      timeouts.push(setTimeout(() => setVisibleRows(i + 1), 600 + i * 450));
    });
    timeouts.push(setTimeout(() => setReady(true), 600 + rows.length * 450 + 500));
    return () => timeouts.forEach(clearTimeout);
  }, [reducedMotion]);

  return (
    <div className="px-3 md:px-6 pb-6 pt-3 space-y-3">
      {/* ReportsHero mock */}
      <div className="glass-subtle shadow-glass rounded-xl border border-border/50 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Période en cours</div>
          <h1 className="text-[15px] font-bold text-foreground">{demoMonthYear({ capitalized: true })} · Carrefour Express Centre</h1>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <div className="text-right">
            <div className="text-muted-foreground">Heures travaillées</div>
            <div className="font-bold text-foreground">523h82</div>
          </div>
          <div className="h-6 w-px bg-border/40" />
          <div className="text-right">
            <div className="text-muted-foreground">Masse salariale</div>
            <div className="font-bold text-foreground">14 218 €</div>
          </div>
        </div>
      </div>

      {/* Pill tabs — scrollable on mobile */}
      <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
        <div className="flex items-center gap-1.5 bg-muted/40 p-1 rounded-full ring-1 ring-border/30 w-fit min-w-max">
          <span className="h-7 px-3 inline-flex items-center gap-1.5 rounded-full bg-background text-foreground text-[11px] font-semibold shadow-sm ring-1 ring-border/40 whitespace-nowrap">
            <PayrollIcon className="h-3 w-3" /> Export Paie
          </span>
          <span className="h-7 px-3 inline-flex items-center gap-1.5 rounded-full text-muted-foreground text-[11px] font-medium whitespace-nowrap">
            <TrendingUp className="h-3 w-3" /> Financier
          </span>
          <span className="h-7 px-3 inline-flex items-center gap-1.5 rounded-full text-muted-foreground text-[11px] font-medium whitespace-nowrap">
            <VerifiedIcon className="h-3 w-3" /> Conformité
          </span>
        </div>
      </div>

      {/* PayrollWorkflow mock */}
      <div className="bg-card border rounded-xl p-3 md:p-3.5 shadow-sm">
        <div className="flex items-start sm:items-center justify-between gap-2 mb-2.5 flex-col sm:flex-row">
          <div className="min-w-0">
            <div className="text-[12px] font-bold text-foreground">
              Préparer la paie {ofMonth(demoMonthName())} {new Date().getFullYear()}
            </div>
            <div className="text-[11px] text-muted-foreground">4 salariés actifs · variables à transmettre au cabinet</div>
          </div>
          <button className="h-7 px-3 rounded-md text-[11px] font-semibold bg-primary text-primary-foreground flex items-center gap-1 shrink-0 self-start sm:self-auto">
            Reprendre l'export
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
          <div className="flex items-center gap-1.5 min-w-max">
            {wizardSteps.map((s, i) => (
              <React.Fragment key={s.id}>
                <div className={`flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[10px] font-medium whitespace-nowrap ${
                  i < 2 ? "bg-success/10 text-success" : i === 2 ? "bg-primary/10 text-primary" : "bg-muted/50 text-muted-foreground"
                }`}>
                  <span className={`w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center ${
                    i < 2 ? "bg-success text-white" : i === 2 ? "bg-primary text-primary-foreground" : "bg-muted-foreground/20 text-muted-foreground"
                  }`}>{s.id}</span>
                  {s.label}
                </div>
                {i < wizardSteps.length - 1 && <ChevronRight className="w-3 h-3 text-muted-foreground/40 shrink-0" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Compliance badge + table */}
      <div className="bg-card border rounded-xl overflow-hidden shadow-sm">
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-muted/15 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-[12px] font-semibold text-foreground">Récapitulatif des heures</div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 border border-success/25 text-[10px] font-medium text-success">
              <ShieldCheck className="w-3 h-3" />
              IDCC 1505
            </span>
          </div>
          <motion.button
            animate={ready ? { scale: [1, 1.04, 1] } : {}}
            transition={{ duration: 1, repeat: ready ? 2 : 0, repeatDelay: 0.5 }}
            className={`h-7 px-3 rounded-md text-[11px] font-semibold flex items-center gap-1.5 shadow-sm shrink-0 ${
              ready ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}
          >
            {ready ? <DownloadIcon className="w-3 h-3" /> : <Loader2 className="w-3 h-3 animate-spin" />}
            <span className="sm:hidden">{ready ? "Export .xlsx" : "Calcul..."}</span>
            <span className="hidden sm:inline">{ready ? "Télécharger l'export (.xlsx)" : "Calcul..."}</span>
          </motion.button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/10 border-b">
              <tr className="text-[10px] text-muted-foreground font-medium">
                <th className="text-left p-2">Salarié</th>
                <th className="text-right p-2">Base</th>
                <th className="text-right p-2">Sup 25%</th>
                <th className="text-right p-2">Sup 50%</th>
                <th className="text-right p-2">Dim.</th>
                <th className="text-right p-2">Variable</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r, i) => (
                <motion.tr
                  key={r.name}
                  initial={reducedMotion ? false : { opacity: 0, x: -12 }}
                  animate={i < visibleRows ? { opacity: 1, x: 0 } : { opacity: 0, x: -12 }}
                  transition={{ duration: 0.3 }}
                >
                  <td className="p-2 font-medium text-foreground">{r.name}</td>
                  <td className="p-2 text-right text-muted-foreground">{r.base}</td>
                  <td className="p-2 text-right">{r.sup25 !== "0h00" ? <span className="font-semibold text-warning">{r.sup25}</span> : <span className="text-muted-foreground">{r.sup25}</span>}</td>
                  <td className="p-2 text-right">{r.sup50 !== "0h00" ? <span className="font-semibold text-destructive">{r.sup50}</span> : <span className="text-muted-foreground">{r.sup50}</span>}</td>
                  <td className="p-2 text-right text-muted-foreground">{r.dim}</td>
                  <td className="p-2 text-right font-bold text-foreground">{r.total}</td>
                </motion.tr>
              ))}
              {Array.from({ length: Math.max(0, rows.length - visibleRows) }).map((_, i) => (
                <tr key={`ph-${i}`}>
                  <td colSpan={6} className="p-2">
                    <div className="h-3.5 bg-muted/40 rounded animate-pulse" />
                  </td>
                </tr>
              ))}
            </tbody>
            {ready && (
              <tfoot>
                <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-primary/5 border-t-2 border-primary/20">
                  <td className="p-2 font-bold text-foreground" colSpan={5}>Total brut variable</td>
                  <td className="p-2 text-right font-bold text-primary">518,02 €</td>
                </motion.tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {ready && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-primary/5 border border-primary/20"
        >
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
            <FileSpreadsheet className="w-3.5 h-3.5 text-primary" />
          </div>
          <div>
            <div className="text-[12px] font-semibold text-foreground">Document prêt à transmettre à votre comptable</div>
            <div className="text-[10px] text-muted-foreground">Récapitulatif des variables de paie du mois</div>
          </div>
        </motion.div>
      )}
    </div>
  );
};
